import React, { useState, useEffect } from 'react';

const PersonDetailsModal = ({ person, gedcomData, onClose, onFindRelationship, onPersonSelect, onSaveEdit }) => {
  // Edit feature temporarily disabled - will be added to roadmap for future consideration
  const [isEditing, setIsEditing] = useState(false);
  const EDIT_FEATURE_ENABLED = false; // Toggle to enable/disable edit functionality
  const [editedPerson, setEditedPerson] = useState({
    name: person?.name || '',
    birth: person?.birth || '',
    death: person?.death || '',
    birthPlace: person?.birthPlace || '',
    deathPlace: person?.deathPlace || '',
    sex: person?.sex || '',
    note: person?.note || ''
  });
  const [saveStatus, setSaveStatus] = useState(null);

  // Update editedPerson when person changes
  useEffect(() => {
    if (person) {
      setEditedPerson({
        name: person.name || '',
        birth: person.birth || '',
        death: person.death || '',
        birthPlace: person.birthPlace || '',
        deathPlace: person.deathPlace || '',
        sex: person.sex || '',
        note: person.note || ''
      });
      setIsEditing(false); // Exit edit mode when person changes
      setSaveStatus(null); // Clear any save status
    }
  }, [person]);

  if (!person) return null;

  const calculateAge = (birth, death) => {
    if (!birth) return null;
    
    const birthYear = parseInt(birth.match(/\d{4}/)?.[0]);
    if (!birthYear) return null;
    
    const endYear = death ? parseInt(death.match(/\d{4}/)?.[0]) : new Date().getFullYear();
    return endYear - birthYear;
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Reset to original values when canceling
      setEditedPerson({
        name: person.name || '',
        birth: person.birth || '',
        death: person.death || '',
        birthPlace: person.birthPlace || '',
        deathPlace: person.deathPlace || '',
        sex: person.sex || '',
        note: person.note || ''
      });
    }
    setIsEditing(!isEditing);
    setSaveStatus(null);
  };

  const handleInputChange = (field, value) => {
    setEditedPerson(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaveStatus('saving');
      
      // Create updated person object
      const updatedPerson = {
        ...person,
        ...editedPerson
      };

      // Call the save callback if provided
      if (onSaveEdit) {
        await onSaveEdit(person.id, updatedPerson);
      }

      // Update local state to reflect changes
      Object.assign(person, editedPerson);
      
      setSaveStatus('success');
      setIsEditing(false);
      
      // Clear success message after 2 seconds
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error('Error saving person data:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const validateDate = (dateStr) => {
    if (!dateStr) return true;
    // Allow various date formats: YYYY, DD MMM YYYY, etc.
    const dateRegex = /^\d{4}$|^\d{1,2}\s+\w{3}\s+\d{4}$|^\w{3}\s+\d{4}$/;
    return dateRegex.test(dateStr.trim());
  };

  const isFormValid = () => {
    return editedPerson.name.trim() && 
           validateDate(editedPerson.birth) && 
           validateDate(editedPerson.death);
  };

  const getRelatives = () => {
    const relatives = {
      parents: [],
      spouses: [],
      children: [],
      siblings: []
    };

    // Find parents
    if (person.parents && gedcomData.families[person.parents]) {
      const parentFamily = gedcomData.families[person.parents];
      if (parentFamily.husband && gedcomData.individuals[parentFamily.husband]) {
        relatives.parents.push(gedcomData.individuals[parentFamily.husband]);
      }
      if (parentFamily.wife && gedcomData.individuals[parentFamily.wife]) {
        relatives.parents.push(gedcomData.individuals[parentFamily.wife]);
      }

      // Find siblings
      if (parentFamily.children) {
        relatives.siblings = parentFamily.children
          .filter(childId => childId !== person.id)
          .map(childId => gedcomData.individuals[childId])
          .filter(Boolean);
      }
    }

    // Find spouses and children
    person.families.forEach(familyId => {
      const family = gedcomData.families[familyId];
      if (family) {
        // Find spouse
        const spouseId = family.husband === person.id ? family.wife : family.husband;
        if (spouseId && gedcomData.individuals[spouseId]) {
          relatives.spouses.push({
            person: gedcomData.individuals[spouseId],
            marriageDate: family.marriage
          });
        }

        // Find children
        if (family.children) {
          const children = family.children
            .map(childId => gedcomData.individuals[childId])
            .filter(Boolean);
          relatives.children.push(...children);
        }
      }
    });

    return relatives;
  };

  const relatives = getRelatives();
  const age = calculateAge(person.birth, person.death);

  return (
    <div className="person-details-modal">
      <div className="person-details-content">
        <div className="person-details-header">
          <div className="person-title">
            <div className={`person-avatar ${(isEditing ? editedPerson.sex : person.sex) === 'M' ? 'male' : 'female'}`}>
              <i className={`fas ${(isEditing ? editedPerson.sex : person.sex) === 'M' ? 'fa-mars' : 'fa-venus'}`}></i>
            </div>
            <div className="person-info">
              <h2>{isEditing ? editedPerson.name || 'Unknown Name' : person.name || 'Unknown Name'}</h2>
              <div className="person-dates">
                {(isEditing ? editedPerson.birth : person.birth) && (
                  <span><i className="fas fa-birthday-cake"></i> Born: {isEditing ? editedPerson.birth : person.birth}</span>
                )}
                {(isEditing ? editedPerson.death : person.death) && (
                  <span><i className="fas fa-cross"></i> Died: {isEditing ? editedPerson.death : person.death}</span>
                )}
                {age && <span><i className="fas fa-hourglass-half"></i> Age: {age} years</span>}
              </div>
            </div>
          </div>
          <div className="header-actions">
            {EDIT_FEATURE_ENABLED && (
              <button 
                className={`edit-btn ${isEditing ? 'editing' : ''}`}
                onClick={handleEditToggle}
                title={isEditing ? 'Cancel Edit' : 'Edit Person'}
              >
                <i className={`fas ${isEditing ? 'fa-times' : 'fa-edit'}`}></i>
              </button>
            )}
            <button onClick={onClose} className="close-btn">×</button>
          </div>
        </div>

        <div className="person-details-body">
          <div className="details-grid">
            {/* Basic Information */}
            <div className="detail-section">
              <h3>
                <i className="fas fa-info-circle"></i> 
                Basic Information
                {/* Edit indicator removed - edit feature temporarily disabled */}
                {EDIT_FEATURE_ENABLED && isEditing && <span className="edit-indicator">✏️ Editing</span>}
              </h3>
              <div className="detail-items">
                <div className="detail-item">
                  <strong>Full Name:</strong>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedPerson.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="edit-input"
                      placeholder="Enter full name"
                      required
                    />
                  ) : (
                    <span>{person.name || 'Unknown'}</span>
                  )}
                </div>
                
                <div className="detail-item">
                  <strong>Gender:</strong>
                  {isEditing ? (
                    <select
                      value={editedPerson.sex}
                      onChange={(e) => handleInputChange('sex', e.target.value)}
                      className="edit-select"
                    >
                      <option value="">Unknown</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  ) : (
                    <span>{person.sex === 'M' ? 'Male' : person.sex === 'F' ? 'Female' : 'Unknown'}</span>
                  )}
                </div>

                <div className="detail-item">
                  <strong>Birth Date:</strong>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedPerson.birth}
                      onChange={(e) => handleInputChange('birth', e.target.value)}
                      className="edit-input"
                      placeholder="e.g., 1866, 15 Mar 1866"
                    />
                  ) : (
                    <span>{person.birth || 'Unknown'}</span>
                  )}
                </div>

                <div className="detail-item">
                  <strong>Birth Place:</strong>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedPerson.birthPlace}
                      onChange={(e) => handleInputChange('birthPlace', e.target.value)}
                      className="edit-input"
                      placeholder="e.g., Italy, Rome, Italy"
                    />
                  ) : (
                    <span>{person.birthPlace || 'Unknown'}</span>
                  )}
                </div>

                <div className="detail-item">
                  <strong>Death Date:</strong>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedPerson.death}
                      onChange={(e) => handleInputChange('death', e.target.value)}
                      className="edit-input"
                      placeholder="e.g., 1958, 20 Dec 1958 (leave empty if living)"
                    />
                  ) : (
                    <span>{person.death || 'Living'}</span>
                  )}
                </div>

                <div className="detail-item">
                  <strong>Death Place:</strong>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedPerson.deathPlace}
                      onChange={(e) => handleInputChange('deathPlace', e.target.value)}
                      className="edit-input"
                      placeholder="e.g., Italy, Toronto, Canada"
                    />
                  ) : (
                    <span>{person.deathPlace || (person.death ? 'Unknown' : 'N/A')}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Family Information */}
            <div className="detail-section">
              <h3><i className="fas fa-users"></i> Family</h3>
              <div className="detail-items">
                {relatives.parents.length > 0 && (
                  <div className="detail-item">
                    <strong>Parents:</strong>
                    <ul className="relatives-list">
                      {relatives.parents.map(parent => (
                        <li key={parent.id}>
                          <button 
                            className="relative-link"
                            onClick={() => onPersonSelect && onPersonSelect(parent)}
                          >
                            {parent.name}
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {relatives.spouses.length > 0 && (
                  <div className="detail-item">
                    <strong>Spouse(s):</strong>
                    <ul className="relatives-list">
                      {relatives.spouses.map((spouse, index) => (
                        <li key={index}>
                          <button 
                            className="relative-link"
                            onClick={() => onPersonSelect && onPersonSelect(spouse.person)}
                          >
                            {spouse.person.name}
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                          {spouse.marriageDate && <span className="marriage-date"> (m. {spouse.marriageDate})</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {relatives.children.length > 0 && (
                  <div className="detail-item">
                    <strong>Children ({relatives.children.length}):</strong>
                    <ul className="relatives-list">
                      {relatives.children.map(child => (
                        <li key={child.id}>
                          <button 
                            className="relative-link"
                            onClick={() => onPersonSelect && onPersonSelect(child)}
                          >
                            {child.name}
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {relatives.siblings.length > 0 && (
                  <div className="detail-item">
                    <strong>Siblings ({relatives.siblings.length}):</strong>
                    <ul className="relatives-list">
                      {relatives.siblings.map(sibling => (
                        <li key={sibling.id}>
                          <button 
                            className="relative-link"
                            onClick={() => onPersonSelect && onPersonSelect(sibling)}
                          >
                            {sibling.name}
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes section */}
          {(person.note || isEditing) && (
            <div className="detail-section full-width">
              <h3><i className="fas fa-sticky-note"></i> Notes</h3>
              {isEditing ? (
                <textarea
                  value={editedPerson.note}
                  onChange={(e) => handleInputChange('note', e.target.value)}
                  className="edit-textarea"
                  placeholder="Add biographical notes, stories, or additional information..."
                  rows="4"
                />
              ) : (
                <div className="person-notes">
                  {person.note}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="person-details-footer">
          {EDIT_FEATURE_ENABLED && isEditing ? (
            <>
              <button 
                className="action-btn save-btn" 
                onClick={handleSave}
                disabled={!isFormValid() || saveStatus === 'saving'}
              >
                <i className={`fas ${saveStatus === 'saving' ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="action-btn cancel-btn" onClick={handleEditToggle}>
                <i className="fas fa-times"></i>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button className="action-btn relationship-btn" onClick={onFindRelationship}>
                <i className="fas fa-route"></i>
                Find Relationship Path
              </button>
              <button className="action-btn close-btn-alt" onClick={onClose}>
                <i className="fas fa-times"></i>
                Close
              </button>
            </>
          )}
          
          {/* Save Status Messages - disabled with edit feature */}
          {EDIT_FEATURE_ENABLED && saveStatus === 'success' && (
            <div className="save-status success">
              <i className="fas fa-check-circle"></i>
              Changes saved successfully!
            </div>
          )}
          {EDIT_FEATURE_ENABLED && saveStatus === 'error' && (
            <div className="save-status error">
              <i className="fas fa-exclamation-circle"></i>
              Error saving changes. Please try again.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonDetailsModal;